import {
  Delete,
  FilterList as FilterListIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PlusOne,
  SearchOff
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks";
import { ExtractionEquipmentForm as ExtractionEquipmentFormType } from "../../../types/forms/capture";
import { ExtractionEquipment } from "../../../types/model/extractionEquipment";
import ExtractionEquipmentForm from "./ExtractionEquipmentForm";

interface RowProps {
  row: ExtractionEquipment;
  open: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onUpdate: (extractionEquipmentId: number, values: ExtractionEquipmentFormType) => Promise<void>;
  onDelete: (extractionEquipmentId: number) => Promise<void>;
}

const Row = ({ row, open, canEdit, onToggle, onUpdate, onDelete }: RowProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const handleError = useErrorHandler();
  const formId = `form-${row.id}`;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async (values: ExtractionEquipmentFormType) => {
    try {
      setIsSaving(true);
      await onUpdate(row.id, values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(row.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };
  
  return (
    <React.Fragment>
      <TableRow onClick={onToggle} sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </TableCell>
        <TableCell component="th" scope="row">
          {row.equipmentType}
        </TableCell>
        <TableCell>{row.equipmentBrandName}</TableCell>
        <TableCell>{row.equipmentDepth} {t("generic.units.m")}</TableCell>
        {canEdit && (
          <TableCell align="right">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              {isEditing ? (
                <>
                  <Tooltip title={t("generic.cancel")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                      disabled={isSaving}
                    >
                      <EditOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("generic.save")}>
                    <IconButton
                      size="small"
                      color="success"
                      type="submit"
                      form={formId}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title={t("generic.edit")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!open) onToggle();
                        setIsEditing(true);
                      }}
                      disabled={isDeleting}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("generic.delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                      disabled={isDeleting}
                    >
                      {isDeleting ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
      <TableRow sx={open ? { backgroundColor: theme.palette.action.hover } : {}}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={canEdit ? 5 : 4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <ExtractionEquipmentForm
                extractionEquipmentData={row}
                formId={formId}
                isEditing={isEditing}
                isSaving={isSaving}
                isDeleting={isDeleting}
                onSubmit={handleSave}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface ExtractionEquipmentsProps {
  extractionEquipmentsData: ExtractionEquipment[];
  canEdit: boolean;
  onCreate: (values: ExtractionEquipmentFormType) => Promise<void>;
  onUpdate: (extractionEquipmentId: number, values: ExtractionEquipmentFormType) => Promise<void>;
  onDelete: (extractionEquipmentId: number) => Promise<void>;
}

export default function ExtractionEquipments({
  extractionEquipmentsData,
  canEdit,
  onCreate,
  onUpdate, 
  onDelete,
}: Readonly<ExtractionEquipmentsProps>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState("");
  const [openRowId, setOpenRowId] = useState<number | null | string>(null);

  const newFormId = "form-new";
  const [isCreating, setIsCreating] = useState(false);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const filteredData = (extractionEquipmentsData || []).filter((input) =>
    input.equipmentType?.toLowerCase().includes(filter.toLowerCase()) ||
    input.equipmentBrandName?.toLowerCase().includes(filter.toLowerCase()) ||
    input.equipmentDepth?.toString().includes(filter.toLowerCase())
  );

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("captures.capture.extraction-equipment.title")}
        </Typography>
        
        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>

        {canEdit && (
          <Tooltip title={t("captures.capture.extraction-equipment.add-new")}>
            <IconButton onClick={() => setOpenRowId(openRowId === 'new' ? null : 'new')}>
              <PlusOne />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>

      {showFilters && (
        <Grid2 container spacing={3} sx={{ p:2 }}>
          <Grid2 size={12}>
            <TextField
              name="name"
              label={t("generic.search")}
              value={filter}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
        </Grid2>
      )}

      <TableContainer>
        <Table aria-label="collapsible table" sx={{ tableLayout: "fixed" }}>
          {openRowId !== 'new' && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "5%" }} />
                <TableCell>{t("captures.capture.extraction-equipment.equipment-type")}</TableCell>
                <TableCell>{t("captures.capture.extraction-equipment.equipment-brand")}</TableCell>
                <TableCell>{t("captures.capture.extraction-equipment.equipment-depth")}</TableCell>
                {canEdit && (
                  <TableCell sx={{ width: "10%" }} align="right">
                    {t("generic.actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {openRowId === 'new' && (
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={canEdit ? 5 : 4}>
                  <Collapse in={true} timeout="auto" unmountOnExit>
                    <Paper sx={{ p: 3, m: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {t("captures.capture.extraction-equipment.add-new")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={t("generic.cancel")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setOpenRowId(null)}
                              disabled={isCreating}
                            >
                              <EditOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("generic.save")}>
                            <IconButton
                              size="small"
                              color="success"
                              type="submit"
                              form={newFormId}
                              disabled={isCreating}
                            >
                              {isCreating ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <ExtractionEquipmentForm
                        formId={newFormId}
                        isEditing={true}
                        isSaving={isCreating}
                        isDeleting={false}
                        onSubmit={async (values) => {
                          try {
                            setIsCreating(true);
                            await onCreate(values);
                            setOpenRowId(null);
                          } finally {
                            setIsCreating(false);
                          }
                        }}
                      />
                    </Paper>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.length === 0 && openRowId !== 'new' && (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} align="center" sx={{ py: 10 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      opacity: 0.6,
                    }}
                  >
                    <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h6">
                      {t("generic.no-records")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((row) => (
              <Row
                key={row.id}
                row={row}
                open={openRowId === row.id}
                canEdit={canEdit}
                onToggle={() => setOpenRowId(openRowId === row.id ? null : row.id)}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}